import {
   BasicFacetTermV2,
   BasicFacetWithTermsV2,
   ExtendedFacetTermV2,
   ExtendedFacetWithTermsV2,
   FacetFilterTerm,
   FacetFilterWithTerms,
   FacetKeysV2
} from '@ravago/shared/page-data/models/elastic';

export const PARENT_KEY_DELIMITER = '---';
export const RANGE_DELIMITER = '__';
export const RANGE_MIN_MAX_DELIMITER = '_';
export const CHILDREN_TERMS_DELIMITER = ',';

export const queryToFilter = (params: string): Array<FacetFilterWithTerms> => {
   const decodedFilter = decodeURIComponent(params);
   return JSON.parse(decodedFilter);
};

const defaultQueryParamTransformer = (facet: string, param: string | string[] | undefined): BasicFacetWithTermsV2 => ({
   facetName: facet,
   terms: param
      ? param
           .toString()
           .split(CHILDREN_TERMS_DELIMITER)
           .map((term) => ({ key: term }))
      : []
});

const nestedQueryParamTransformer = (facet: string, param: string | string[] | undefined): BasicFacetWithTermsV2 => ({
   facetName: facet,
   terms: param
      ? param
           .toString()
           .split(CHILDREN_TERMS_DELIMITER)
           .map((term) => {
              const [child, parent] = term.split(PARENT_KEY_DELIMITER);

              return {
                 key: child,
                 parentKey: parent
              };
           })
      : []
});

const rangeQueryParamTransformer = (facet: string, param: string | string[] | undefined): BasicFacetWithTermsV2 => ({
   facetName: facet,
   terms: param
      ? param
           .toString()
           .split(CHILDREN_TERMS_DELIMITER)
           .map((term) => {
              const splitTerm = term.toString().split(RANGE_DELIMITER);
              if (splitTerm.length < 2) {
                 return { key: term };
              }
              const range = splitTerm[1];
              const [min, max] = range.split(RANGE_MIN_MAX_DELIMITER);
              const minNumber = Number(min);
              const maxNumber = Number(max);
              if (Number.isNaN(minNumber) || Number.isNaN(maxNumber)) {
                 return { key: splitTerm[0] };
              }
              return { key: splitTerm[0], range: { min: minNumber, max: maxNumber } };
           })
      : []
});

const QueryParamTransformersV2: {
   [K in FacetKeysV2]: (acc: BasicFacetWithTermsV2[], param: string | string[] | undefined) => void;
} = {
   country: (acc: BasicFacetWithTermsV2[], param: string | string[] | undefined) => {
      acc.push(defaultQueryParamTransformer('country', param));
   },
   brand: (acc: BasicFacetWithTermsV2[], param: string | string[] | undefined) => {
      acc.push(defaultQueryParamTransformer('brand', param));
   },
   producer: (acc: BasicFacetWithTermsV2[], param: string | string[] | undefined) => {
      acc.push(defaultQueryParamTransformer('producer', param));
   },
   group: (acc: BasicFacetWithTermsV2[], param: string | string[] | undefined) => {
      acc.push(defaultQueryParamTransformer('group', param));
   },
   subgroup: (acc: BasicFacetWithTermsV2[], param: string | string[] | undefined) => {
      acc.push(defaultQueryParamTransformer('subgroup', param));
   },
   quality: (acc: BasicFacetWithTermsV2[], param: string | string[] | undefined) => {
      acc.push(defaultQueryParamTransformer('quality', param));
   },
   feature: (acc: BasicFacetWithTermsV2[], param: string | string[] | undefined) => {
      acc.push(defaultQueryParamTransformer('feature', param));
   },
   visibility: (acc: BasicFacetWithTermsV2[], param: string | string[] | undefined) => {
      acc.push(defaultQueryParamTransformer('visibility', param));
   },
   filler: (acc: BasicFacetWithTermsV2[], param: string | string[] | undefined) => {
      acc.push(rangeQueryParamTransformer('filler', param));
   },
   industry: (acc: BasicFacetWithTermsV2[], param: string | string[] | undefined) => {
      acc.push(defaultQueryParamTransformer('industry', param));
   },
   'processing-method': (acc: BasicFacetWithTermsV2[], param: string | string[] | undefined) => {
      acc.push(defaultQueryParamTransformer('processing-method', param));
   },
   segment: (acc: BasicFacetWithTermsV2[], param) => {
      acc.push(nestedQueryParamTransformer('segment', param));
   },
   'sustainability-group': (acc: BasicFacetWithTermsV2[], param: string | string[] | undefined) => {
      acc.push(defaultQueryParamTransformer('sustainability-group', param));
   },
   'sub-sustainability': (acc: BasicFacetWithTermsV2[], param: string | string[] | undefined) => {
      acc.push(nestedQueryParamTransformer('sub-sustainability', param));
   },
   'flame-rating': (acc: BasicFacetWithTermsV2[], param: string | string[] | undefined): void => {
      acc.push(nestedQueryParamTransformer('flame-rating', param));
   },
   density: (acc: BasicFacetWithTermsV2[], param: string | string[] | undefined) => {
      acc.push(rangeQueryParamTransformer('density', param));
   },
   'flow-rate': (acc: BasicFacetWithTermsV2[], param: string | string[] | undefined) => {
      acc.push(rangeQueryParamTransformer('flow-rate', param));
   },
   'notched-impact': (acc: BasicFacetWithTermsV2[], param: string | string[] | undefined) => {
      acc.push(rangeQueryParamTransformer('notched-impact', param));
   },
   'notched-impact-strength': (acc: BasicFacetWithTermsV2[], param: string | string[] | undefined) => {
      acc.push(rangeQueryParamTransformer('notched-impact-strength', param));
   }
};

const queryToBasicFacetWithTermsMapperV2 = (params: { [key: string]: string | string[] | undefined }): BasicFacetWithTermsV2[] => {
   const facets: BasicFacetWithTermsV2[] = [];
   (Object.keys(params) as FacetKeysV2[]).forEach((key) => {
      if (key in QueryParamTransformersV2) QueryParamTransformersV2[key](facets, params[key]);
   });
   return facets;
};

export const queryToFacetsV2 = (params: { [key: string]: string | string[] | undefined }): BasicFacetWithTermsV2[] =>
   queryToBasicFacetWithTermsMapperV2(params);

export const filterToQuery = (filters: FacetFilterWithTerms[]): string => {
   const serializedFilter = JSON.stringify(filters);
   return encodeURIComponent(serializedFilter);
};

export const termsToQueryV2 = (terms: BasicFacetTermV2[]): string =>
   terms
      .map(
         (term) =>
            `${term.key}${term.parentKey ? `${PARENT_KEY_DELIMITER}${term.parentKey}` : ''}${
               term.range ? `${RANGE_DELIMITER}${term.range.min}${RANGE_MIN_MAX_DELIMITER}${term.range.max}` : ''
            }`
      )
      .join(CHILDREN_TERMS_DELIMITER);

const extendedFacetToBasicFacetV2 = (term: ExtendedFacetTermV2): BasicFacetTermV2 => ({
   key: term.key,
   ...(term.range && { range: term.range }),
   ...(term.parentKey && { parentKey: term.parentKey })
});

const mergeTerms = (target: BasicFacetTermV2[], incoming: BasicFacetTermV2[]) => {
   incoming.forEach((term) => {
      const exists = target.some((t) => t.key === term.key && t.parentKey === term.parentKey);
      if (!exists) target.push(term);
   });
};

const extractBasicFacetsWithTermsV2 = (filters: BasicFacetWithTermsV2[]): BasicFacetWithTermsV2[] => {
   const allTerms: BasicFacetWithTermsV2[] = [];

   const upsertFacet = (facet: string, terms: BasicFacetTermV2[]) => {
      if (!terms || terms.length === 0) return;
      const existing = allTerms.find((f) => f.facetName === facet);
      if (existing) {
         mergeTerms(existing.terms, terms);
      } else {
         allTerms.push({ facetName: facet, terms: [...terms] });
      }
   };

   const walkBasic = (basic: BasicFacetWithTermsV2) => {
      upsertFacet(basic.facetName, basic.terms as BasicFacetTermV2[]);

      (basic.terms as BasicFacetTermV2[]).forEach((term: any) => {
         if (term?.nested?.length) {
            (term.nested as BasicFacetWithTermsV2[]).forEach(walkBasic);
         }
      });
   };

   filters.forEach(walkBasic);
   return allTerms;
};

export const basicFacetWithTermsToQueryV2 = (facets: BasicFacetWithTermsV2[]): string[][] => {
   const allFacets = extractBasicFacetsWithTermsV2(facets);
   return allFacets.map((facet) => [facet.facetName, termsToQueryV2(facet.terms)]);
};

export const extendedFacetWithTermsToBasicFacetWithTermsV2 = (facets: ExtendedFacetWithTermsV2[]): BasicFacetWithTermsV2[] => {
   const basicFacetsWithTerms: BasicFacetWithTermsV2[] = [];
   facets.forEach((facet) => {
      facet.terms.forEach((term) => {
         if (term.nested && term.nested.length > 0) {
            term.nested.forEach((nestedFacet) => {
               const transformedNestedFacet = nestedFacet.terms.map((nestedFacetTerm) => extendedFacetToBasicFacetV2(nestedFacetTerm));
               const existingFacet = basicFacetsWithTerms.find((f) => f.facetName === nestedFacet.facetName);
               if (existingFacet) {
                  mergeTerms(existingFacet.terms, transformedNestedFacet);
               } else {
                  basicFacetsWithTerms.push({
                     facetName: nestedFacet.facetName,
                     terms: transformedNestedFacet
                  });
               }
            });
         } else {
            const transformedTerm = extendedFacetToBasicFacetV2(term);
            const existingFacet = basicFacetsWithTerms.find((f) => f.facetName === facet.facetName);
            if (existingFacet) {
               mergeTerms(existingFacet.terms, [transformedTerm]);
            } else {
               basicFacetsWithTerms.push({
                  facetName: facet.facetName,
                  terms: [transformedTerm]
               });
            }
         }
      });
   });
   return basicFacetsWithTerms;
};

export const extendedFacetWithTermsToQueryV2 = (facets: ExtendedFacetWithTermsV2[]): string[][] => {
   const basicFacetsWithTerms: BasicFacetWithTermsV2[] = extendedFacetWithTermsToBasicFacetWithTermsV2(facets);

   const allFacets = extractBasicFacetsWithTermsV2(basicFacetsWithTerms);
   return allFacets.map((facet) => [facet.facetName, termsToQueryV2(facet.terms)]);
};

const propMap: Record<string, string> = {
   countries: 'sellingCountries',
   polymerGroups: 'group.raw',
   polymerTypes: 'subgroup.raw',
   specialFeatures: 'features.raw',
   fillers: 'fillers.name.raw',
   technologies: 'processingMethods.name.raw',
   brands: 'brand.raw',
   producers: 'producer.name.raw',
   qualities: 'quality.raw',
   markets: 'industries.name.raw'
};

const transformProperty = (parts: string[]): FacetFilterTerm[] => parts.map((part) => ({ term: part }));

const transformNestedProperty = (nestedFacetName: string, parts: string[]): FacetFilterTerm[] =>
   parts.reduce((acc: FacetFilterTerm[], part) => {
      if (!part.includes('---')) {
         acc.push({ term: part });
         return acc;
      }

      const [mainTerm, nestedTerm] = part.split('---');

      const existingTerm = acc.find((item) => item.term === mainTerm);
      const nestedFacet = existingTerm && existingTerm.nestedFacets ? existingTerm.nestedFacets[0] : undefined;

      if (nestedFacet) {
         nestedFacet.terms.push({ term: nestedTerm });
      } else {
         acc.push({
            term: mainTerm,
            nestedFacets: [{ facet: nestedFacetName, terms: [{ term: nestedTerm }] }]
         });
      }
      return acc;
   }, []);

const transformPropertyWithRange = (parts: string[]): FacetFilterTerm[] =>
   parts.reduce((acc: FacetFilterTerm[], part) => {
      if (!part.includes('---')) {
         return acc;
      }

      const [mainTerm, rangeTerm] = part.split('---');
      const [min, max] = rangeTerm.split('-');

      acc.push({ term: mainTerm, range: { min: Number(min), max: Number(max) } });

      return acc;
   }, []);

const getTerms = (property: string, parts: string[]) => {
   if (property === 'markets') return transformNestedProperty('industries.segments.name.raw', parts);
   if (property === 'fillers') return transformPropertyWithRange(parts);
   return transformProperty(parts);
};

const createFilter = (property: string, value: string | string[] | undefined): FacetFilterWithTerms | undefined => {
   if (!(property in propMap) || !value || Array.isArray(value)) return undefined;

   const parts = value.split(';');
   const terms = getTerms(property, parts);

   return {
      facet: propMap[property],
      terms
   };
};

export const legacyQueryToFilter = (params: Record<string, string | string[] | undefined>): FacetFilterWithTerms[] => {
   const facetFilters: FacetFilterWithTerms[] = [];

   Object.entries(params).forEach(([key, value]) => {
      const facetFilter = createFilter(key, value);
      if (facetFilter) facetFilters.push(facetFilter);
   });

   return facetFilters;
};
